/**
 * Available themes for each property.
 */
export const THEMES = Object.freeze({
  wsj: Object.freeze({
    default: Object.freeze(['wsj_world', 'wsj_biztech', 'wsj_mibizfin', 'wsj_op']),
    pie: null,
  }),
  barrons: Object.freeze({
    default: null,
    pie: Object.freeze(['barrons_blue', 'barrons_green', 'barrons_gray']),
  }),
  djriskjournal: Object.freeze({
    default: Object.freeze([
      'djriskjournal',
      'djriskjournal_red',
      'djriskjournal_green',
      'djriskjournal_gold',
    ]),
    pie: null,
  }),
  marketwatch: Object.freeze({
    default: null,
    pie: Object.freeze(['marketwatch_blue', 'marketwatch_green', 'marketwatch_gray']),
  }),
  mansionglobal: Object.freeze({
    default: null,
    pie: Object.freeze(['mansionglobal_blue', 'mansionglobal_gray']),
  }),
  fnlondon: Object.freeze({
    default: Object.freeze(['fnlondon', 'pen']),
    pie: Object.freeze(['fnlondon_gray', 'fnlondon_orange']),
  }),
});

/**
 * An object containing color theme for every chart type
 * divided by publication
 */
export const colorThemesByType = Object.freeze({
  wsj: Object.freeze({
    line: THEMES.wsj.default,
    column: THEMES.wsj.default,
    bar: THEMES.wsj.default,
    area: THEMES.wsj.default,
    step: THEMES.wsj.default,
    scatter: THEMES.wsj.default,
    pie: THEMES.wsj.pie,
  }),
  barrons: Object.freeze({
    line: THEMES.barrons.default,
    column: THEMES.barrons.default,
    bar: THEMES.barrons.default,
    area: THEMES.barrons.default,
    step: THEMES.barrons.default,
    scatter: THEMES.barrons.default,
    pie: THEMES.barrons.pie,
  }),
  djriskjournal: Object.freeze({
    line: THEMES.djriskjournal.default,
    column: THEMES.djriskjournal.default,
    bar: THEMES.djriskjournal.default,
    area: THEMES.djriskjournal.default,
    step: THEMES.djriskjournal.default,
    scatter: THEMES.djriskjournal.default,
    pie: THEMES.djriskjournal.pie,
  }),
  marketwatch: Object.freeze({
    line: THEMES.marketwatch.default,
    column: THEMES.marketwatch.default,
    bar: THEMES.marketwatch.default,
    area: THEMES.marketwatch.default,
    step: THEMES.marketwatch.default,
    scatter: THEMES.marketwatch.default,
    pie: THEMES.marketwatch.pie,
  }),
  mansionglobal: Object.freeze({
    line: THEMES.mansionglobal.default,
    column: THEMES.mansionglobal.default,
    bar: THEMES.mansionglobal.default,
    area: THEMES.mansionglobal.default,
    step: THEMES.mansionglobal.default,
    scatter: THEMES.mansionglobal.default,
    pie: THEMES.mansionglobal.pie,
  }),
  fnlondon: Object.freeze({
    line: THEMES.fnlondon.default,
    column: THEMES.fnlondon.default,
    bar: THEMES.fnlondon.default,
    area: THEMES.fnlondon.default,
    step: THEMES.fnlondon.default,
    scatter: THEMES.fnlondon.default,
    pie: THEMES.fnlondon.pie,
  }),
});

/**
 * A rule describing whether or not to show an opacity
 */
export const showOpacity = Object.freeze({
  wsj: Object.freeze({
    line: false,
    column: false,
    bar: false,
    area: false,
    step: false,
    scatter: true,
    pie: false,
  }),
  barrons: Object.freeze({
    line: false,
    column: false,
    bar: false,
    area: false,
    step: false,
    scatter: true,
    pie: false,
  }),
  djriskjournal: Object.freeze({
    line: false,
    column: false,
    bar: false,
    area: false,
    step: false,
    scatter: true,
    pie: false,
  }),
  marketwatch: Object.freeze({
    line: false,
    column: false,
    bar: false,
    area: false,
    step: false,
    scatter: true,
    pie: false,
  }),
  mansionglobal: Object.freeze({
    line: false,
    column: false,
    bar: false,
    area: false,
    step: false,
    scatter: true,
    pie: false,
  }),
  fnlondon: Object.freeze({
    line: false,
    column: false,
    bar: false,
    area: false,
    step: false,
    scatter: true,
    pie: false,
  }),
});

/**
 * A rule describing whether or not to show color selector
 */
export const showColorSelector = Object.freeze({
  wsj: Object.freeze({
    line: false,
    column: true,
    bar: true,
    area: false,
    step: false,
    scatter: false,
    pie: false,
  }),
  barrons: Object.freeze({
    line: false,
    column: true,
    bar: true,
    area: false,
    step: false,
    scatter: false,
    pie: false,
  }),
  djriskjournal: Object.freeze({
    line: false,
    column: true,
    bar: true,
    area: false,
    step: false,
    scatter: false,
    pie: false,
  }),
  marketwatch: Object.freeze({
    line: false,
    column: true,
    bar: true,
    area: false,
    step: false,
    scatter: false,
    pie: false,
  }),
  mansionglobal: Object.freeze({
    line: false,
    column: true,
    bar: true,
    area: false,
    step: false,
    scatter: false,
    pie: false,
  }),
  fnlondon: Object.freeze({
    line: false,
    column: true,
    bar: true,
    area: false,
    step: false,
    scatter: false,
    pie: false,
  }),
});

/**
 * A rule describing whether or not to show color accordion
 */
export const showColorAccordion = Object.freeze({
  wsj: Object.freeze({
    line: true,
    column: true,
    bar: true,
    area: true,
    step: true,
    scatter: true,
    pie: false,
  }),
  barrons: Object.freeze({
    line: true,
    column: true,
    bar: true,
    area: true,
    step: true,
    scatter: true,
    pie: false,
  }),
  djriskjournal: Object.freeze({
    line: true,
    column: true,
    bar: true,
    area: true,
    step: true,
    scatter: true,
    pie: false,
  }),
  marketwatch: Object.freeze({
    line: true,
    column: true,
    bar: true,
    area: true,
    step: true,
    scatter: true,
    pie: false,
  }),
  mansionglobal: Object.freeze({
    line: true,
    column: true,
    bar: true,
    area: true,
    step: true,
    scatter: true,
    pie: false,
  }),
  fnlondon: Object.freeze({
    line: true,
    column: true,
    bar: true,
    area: true,
    step: true,
    scatter: true,
    pie: false,
  }),
});
